// Utilidades compartidas del módulo Gestión Clínica (esquema medical).
// Pacientes y médicos son partners (financiero.partner); las tablas medical
// guardan la extensión clínica/profesional. El alta/edición se hace por RPC
// (partner + extensión, atómico); las sub-tablas por REST directo.
import { useLayoutEffect, useMemo, useRef } from 'react';
import type { TextareaHTMLAttributes } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Image as ImageIcon, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api/client';
import { Campo as CampoBase, fieldCls } from '@/features/inventario/shared';
import { LOCALE } from '@/lib/utils/regional';

// Reutilizamos los helpers de UI del módulo de inventario (mismo estándar).
export { Modal, Campo, Switch, fieldCls } from '@/features/inventario/shared';
import type { CabeceraImpreso } from './impreso';
export type { CabeceraImpreso };

// Textarea que se AUTOAJUSTA al contenido: crece al escribir Y al montar con
// texto existente (nunca deja contenido oculto tras un scroll interno), y
// CONSERVA el agarre nativo de la esquina (resize vertical): arrastrarlo fija
// una altura mínima preferida — espacio extra para seguir escribiendo — desde
// la cual el autoajuste sigue creciendo; arrastrar más chico baja hasta el
// piso del contenido (nunca se corta texto). `minRows` = altura mínima base;
// NO pasar clases h-*. El +2 compensa los bordes (box-sizing: border-box).
export function AutoTextarea({ minRows = 2, className = '', ...props }:
  TextareaHTMLAttributes<HTMLTextAreaElement> & { minRows?: number }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const minManual = useRef(0);     // altura preferida fijada arrastrando el agarre
  const alturaAuto = useRef(0);    // última altura aplicada por el autoajuste
  const ajustar = () => {
    const el = ref.current;
    // Oculto (pestaña keep-alive display:none / modal sin layout): scrollHeight
    // mediría 0 y la caja quedaría en minRows al CONSULTAR un registro con
    // texto — no medir aquí; el ResizeObserver de abajo re-mide al aparecer.
    if (!el || el.getClientRects().length === 0) return;
    el.style.height = 'auto';
    const h = Math.max(el.scrollHeight + 2, minManual.current);
    el.style.height = `${h}px`;
    alturaAuto.current = h;
  };
  useLayoutEffect(ajustar, [props.value]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Re-medir cuando la caja APARECE (ancho pasa de 0 al abrir la pestaña/modal)
  // o cambia de ancho (menos ancho = más líneas). Solo reacciona a CAMBIOS DE
  // ANCHO: ajustar() cambia la altura y dispararía el observer en bucle.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let anchoPrevio = -1;
    const ro = new ResizeObserver((entries) => {
      const ancho = Math.round(entries[0]?.contentRect.width ?? 0);
      if (ancho > 0 && ancho !== anchoPrevio) { anchoPrevio = ancho; ajustar(); }
    });
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Fin de un arrastre del agarre: si la altura difiere de la aplicada, fue
  // redimensión manual → se vuelve el nuevo mínimo (con piso en el contenido).
  const finArrastre = () => {
    const el = ref.current;
    if (!el) return;
    if (Math.abs(el.offsetHeight - alturaAuto.current) > 4) {
      minManual.current = el.offsetHeight;
      ajustar();
    }
  };
  // fieldCls trae h-10 FIJO: se filtra (en vez de pisarlo con !h-auto) porque
  // `!h-auto` = height:auto !important, que le GANA al height inline en px del
  // autoajuste — la caja se quedaba en minRows al escribir (bug detectado en
  // Pacientes → Otros → Alergias). Sin clases de altura, manda el inline (y el
  // arrastre del agarre, que también escribe inline, sigue funcionando).
  const claseBase = fieldCls.replace(/(^|\s)h-\d+(\s|$)/g, ' ');
  return (
    <textarea ref={ref} rows={minRows} onPointerUp={finArrastre}
      className={`${claseBase} resize-y overflow-hidden ${className}`} {...props} />
  );
}

// Subida de PDF sin backend: se guarda incrustado (data URL base64) en la columna.
export function ArchivoUpload({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const leer = (file: File) => {
    if (file.type !== 'application/pdf') { toast.error('El archivo debe ser PDF'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('El PDF supera 2 MB; usa uno más liviano'); return; }
    const r = new FileReader();
    r.onload = () => onChange(String(r.result));
    r.onerror = () => toast.error('No se pudo leer el archivo');
    r.readAsDataURL(file);
  };
  return (
    <div className="flex items-center gap-2">
      {value
        ? <a href={value} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">Ver PDF</a>
        : <span className="text-xs text-muted-foreground">Sin PDF</span>}
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted">
        <Upload className="h-3.5 w-3.5" /> {value ? 'Cambiar' : 'Subir'}
        <input type="file" accept="application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) leer(f); e.target.value = ''; }} />
      </label>
      {value && <button type="button" className="text-xs text-destructive hover:underline" onClick={() => onChange('')}>Quitar</button>}
    </div>
  );
}

// Catálogo de países (RPC med_paises) — nacionalidad + código telefónico.
export interface Pais { codigo: string; nombre: string; nacionalidad: string; codigo_telefono: string | null }

// Teléfono con selector de código de país (+593 por defecto). Guarda "+593 099…".
export function TelefonoInput({ value, onChange, paises }: { value: string; onChange: (v: string) => void; paises: Pais[] }) {
  const codigos = useMemo(() => {
    const m = new Map<string, string>();
    paises.forEach((p) => { if (p.codigo_telefono && !m.has(p.codigo_telefono)) m.set(p.codigo_telefono, `${p.codigo} ${p.codigo_telefono}`); });
    if (!m.has('+593')) m.set('+593', 'EC +593');
    return [...m.entries()];
  }, [paises]);
  const code = codigos.map(([c]) => c).sort((a, b) => b.length - a.length).find((c) => value.startsWith(c)) || '+593';
  const num = value.startsWith(code) ? value.slice(code.length).trim() : value;
  const combinar = (c: string, n: string) => (n.trim() ? `${c} ${n.trim()}` : '');
  return (
    <div className="flex gap-1">
      {/* !w-24: fieldCls trae w-full y sin el important el select se estiraba
          (conflicto de clases Tailwind) descuadrando el par código+número */}
      <select className={`${fieldCls} !w-24 shrink-0 !px-1.5`} value={code} onChange={(e) => onChange(combinar(e.target.value, num))}>
        {codigos.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
      </select>
      <input className={`${fieldCls} flex-1 min-w-0`} value={num} onChange={(e) => onChange(combinar(code, e.target.value))} placeholder="099 123 4567" />
    </div>
  );
}

// Subida de imagen sin backend: lee el archivo y lo guarda incrustado (data URL
// base64) en la columna correspondiente. Reutilizable (foto, firma, logo, etc.).
export function ImageUpload({ value, onChange, label, ancho = false }: { value: string; onChange: (v: string) => void; label: string; ancho?: boolean }) {
  const leer = (file: File) => {
    if (!file.type.startsWith('image/')) { toast.error('El archivo debe ser una imagen'); return; }
    if (file.size > 512 * 1024) { toast.error('La imagen supera 512 KB; usa una más liviana'); return; }
    const r = new FileReader();
    r.onload = () => onChange(String(r.result));
    r.onerror = () => toast.error('No se pudo leer el archivo');
    r.readAsDataURL(file);
  };
  const box = ancho ? 'h-12 w-28' : 'h-14 w-14';
  return (
    <CampoBase label={label}>
      <div className="flex items-center gap-3">
        {value
          ? <img src={value} alt={label} className={`rounded-md border bg-background object-contain ${box}`} />
          : <div className={`flex items-center justify-center rounded-md border bg-muted text-muted-foreground ${box}`}><ImageIcon className="h-5 w-5" /></div>}
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted">
          <Upload className="h-4 w-4" /> Subir
          <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) leer(f); e.target.value = ''; }} />
        </label>
        {value && <button type="button" className="text-xs text-destructive hover:underline" onClick={() => onChange('')}>Quitar</button>}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">PNG, JPG o SVG, &lt; 512 KB (se guarda en la base).</p>
    </CampoBase>
  );
}

const MED = { headers: { 'Content-Profile': 'medical', 'Accept-Profile': 'medical' } };
const repr = { headers: { Prefer: 'return=representation' } };

// RPCs del esquema medical (requieren el header de profile)
export const medRpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body, MED)).data;

// CRUD REST sobre tablas medical (el interceptor /rest añade los profiles)
const base = (t: string) => `/rest/medical.${t}`;
export const med = {
  list: async <T,>(t: string, query = ''): Promise<T[]> => (await apiClient.get<T[]>(`${base(t)}${query}`)).data,
  create: async (t: string, body: unknown) => (await apiClient.post(base(t), body, repr)).data,
  update: async (t: string, id: string, body: unknown) => (await apiClient.patch(`${base(t)}?id=eq.${id}`, body, repr)).data,
  remove: async (t: string, id: string) => (await apiClient.delete(`${base(t)}?id=eq.${id}`)).data,
};

// ---- Tipos ----
export interface Especialidad {
  id: string; codigo: string | null; nombre: string; descripcion: string | null;
  duracion_min: number; es_activo: boolean;
}

export interface PacienteRow {
  id: string; partner_id: string; codigo: string | null;
  fecha_nacimiento: string | null; genero: string | null; tipo_sangre: string | null;
  alergias: string | null; aseguradora: string | null; es_activo: boolean;
  razon_social: string; identificacion: string | null; tipo_identificacion: string | null;
  email: string | null; telefono: string | null; celular: string | null;
  centro_costo_id: string | null; centro_codigo: string | null; centro_nombre: string | null;
}

export interface MedicoRow {
  id: string; partner_id: string; especialidad_id: string | null;
  numero_licencia: string | null; titulo: string | null;
  precio_consulta: number; duracion_consulta: number; acepta_telematica: boolean;
  foto_url: string | null; firma_url: string | null; firma_modo?: string | null; logo_url?: string | null; es_activo: boolean;
  razon_social: string; identificacion: string | null;
  email: string | null; telefono: string | null; celular: string | null;
  especialidad: string | null; especialidad_sexo?: string | null;
  rating?: number; n_calificaciones?: number;
}

export interface PortalCita {
  id: string; fecha: string; hora_inicio: string; estado: string; tipo_atencion: string;
  medico: string; especialidad: string | null; cancelable: boolean; calificada: boolean;
}
export interface MiSolicitudReceta { id: string; descripcion: string; estado: string; respuesta: string | null; created_at: string; resuelta_at: string | null; receta_id: string | null }
export interface SolicitudRecetaRow {
  id: string; descripcion: string; estado: string; respuesta: string | null; created_at: string;
  receta_origen_id: string | null; receta_id: string | null; paciente: string; medico: string | null;
  receta_origen_codigo: string | null; origen_renovable: boolean | null;
}

export interface Familiar {
  id: string; paciente_id: string; nombre: string; parentesco: string | null;
  identificacion: string | null; telefono: string | null; email: string | null;
  es_tutor: boolean; observaciones: string | null;
}

// ---- Consentimiento informado (LOPDP) ----
export interface ConsentItem { clave: string; etiqueta: string; requerido?: boolean }
export interface ConsentModelo {
  id: string; tipo: string; version: string; titulo: string; texto: string;
  items: ConsentItem[]; vigente: boolean; created_at: string; updated_at: string;
}
export interface ConsentRegistro {
  id: string; paciente_id: string; tipo: string; base_legal: string; version: string | null;
  items: ConsentItem[]; otorgante: string; otorgante_nombre: string | null;
  otorgante_relacion: string | null; medio: string; tiene_firma: boolean;
  aceptado: boolean; aceptado_at: string; vence_at: string | null;
  revocado: boolean; revocado_at: string | null; revocado_motivo: string | null;
  observaciones: string | null; created_at: string;
}
export const TIPO_CONSENTIMIENTO: Record<string, string> = {
  datos_salud: 'Datos de salud',
  comunicaciones: 'Comunicaciones',
  compartir_terceros: 'Compartir con terceros',
  investigacion: 'Investigación',
  imagenes: 'Imágenes clínicas',
};
// ---- Derechos ARCO (LOPDP) ----
export interface SolicitudArco {
  id: string; paciente_id: string; tipo: string; estado: string; canal: string;
  descripcion: string | null; fecha_limite: string; respuesta: string | null;
  resuelta_at: string | null; created_at: string; paciente: string;
}
export const TIPO_ARCO: Record<string, string> = {
  acceso: 'Acceso', rectificacion: 'Rectificación', eliminacion: 'Eliminación',
  oposicion: 'Oposición', portabilidad: 'Portabilidad', suspension: 'Suspensión',
};
export const ESTADO_ARCO: Record<string, { label: string; cls: string }> = {
  pendiente: { label: 'Pendiente', cls: 'bg-pendiente-soft text-pendiente' },
  en_proceso: { label: 'En proceso', cls: 'bg-estable-soft text-estable' },
  atendida: { label: 'Atendida', cls: 'bg-completado-soft text-completado' },
  rechazada: { label: 'Rechazada', cls: 'bg-urgente-soft text-urgente' },
};

export const BASE_LEGAL: Record<string, string> = {
  consentimiento: 'Consentimiento',
  interes_vital: 'Interés vital',
  obligacion_legal: 'Obligación legal',
  medicina_preventiva_laboral: 'Medicina preventiva/laboral',
  diagnostico_medico: 'Diagnóstico médico',
  salud_publica: 'Salud pública',
  contrato: 'Contrato',
};

export interface Horario {
  id: string; medico_id: string; dia_semana: number; hora_inicio: string; hora_fin: string;
  tipo_atencion: string; consultorio: string | null; es_activo: boolean;
}

export interface Ausencia {
  id: string; medico_id: string; tipo: string; fecha_inicio: string; fecha_fin: string;
  todo_el_dia: boolean; hora_inicio: string | null; hora_fin: string | null; motivo: string | null;
}

export const GENEROS = [
  { v: 'M', l: 'Masculino' }, { v: 'F', l: 'Femenino' }, { v: 'O', l: 'Otro' },
];
export const TIPO_ATENCION = ['presencial', 'telematica', 'domiciliaria', 'urgencia'];
export const TIPO_AUSENCIA = ['vacaciones', 'ausencia', 'licencia', 'feriado', 'capacitacion', 'otro'];
export const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

// ---- Agenda / Citas ----
export interface Cita {
  id: string; fecha: string; hora_inicio: string; hora_fin: string;
  tipo_atencion: string; estado: string; consultorio: string | null; motivo: string | null;
  medico_id: string; paciente_id: string; especialidad_id: string | null;
  checkin_at: string | null; confirmada_at: string | null;
  paciente: string; paciente_ident: string | null; medico: string; especialidad: string | null;
}

export interface Slot {
  hora_inicio: string; hora_fin: string; tipo_atencion: string;
  consultorio: string | null; disponible: boolean;
}

export interface EsperaRow {
  id: string; paciente_id: string; medico_id: string | null; especialidad_id: string | null;
  tipo_atencion: string; prioridad: number; fecha_deseada: string | null; estado: string;
  notas: string | null; created_at: string;
  paciente: string; paciente_tel: string | null; medico: string | null; especialidad: string | null;
}

export const ESTADO_CITA: Record<string, { label: string; cls: string }> = {
  programada: { label: 'Programada', cls: 'bg-secondary text-secondary-foreground' },
  confirmada: { label: 'Confirmada', cls: 'bg-estable-soft text-estable' },
  en_espera: { label: 'En espera', cls: 'bg-pendiente-soft text-pendiente' },
  en_atencion: { label: 'En atención', cls: 'bg-primary/15 text-primary' },
  atendida: { label: 'Atendida', cls: 'bg-completado-soft text-completado' },
  cancelada: { label: 'Cancelada', cls: 'bg-urgente-soft text-urgente' },
  no_asistio: { label: 'No asistió', cls: 'bg-muted text-muted-foreground' },
};

// ---- Helpers de fecha (sin librerías; semana inicia lunes) ----
export const isoDate = (d: Date): string => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};
export const parseISO = (s: string): Date => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
export const addDays = (s: string, n: number): string => { const d = parseISO(s); d.setDate(d.getDate() + n); return isoDate(d); };
export const addMonths = (s: string, n: number): string => { const d = parseISO(s); d.setMonth(d.getMonth() + n); return isoDate(d); };
export const startOfWeek = (s: string): string => { const d = parseISO(s); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return isoDate(d); };
export const hhmm = (t: string | null): string => (t ? t.slice(0, 5) : '');
export const fmtFecha = (s: string): string => parseISO(s).toLocaleDateString(LOCALE, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
export const DIAS_CORTOS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// ---- Historia Clínica (HCE) ----
export interface Cie10 { codigo: string; descripcion: string; capitulo: string | null }

export interface SoapContenido {
  motivo?: string; subjetivo?: string; objetivo?: string; analisis?: string; plan?: string;
}

// Motor de formatos clínicos: schema dinámico guardado en plantilla.contenido
export type TipoCampo = 'texto' | 'textarea' | 'numero' | 'fecha' | 'select' | 'checkbox' | 'titulo';
export interface CampoFormato {
  clave: string; etiqueta: string; tipo: TipoCampo;
  opciones?: string[]; unidad?: string; requerido?: boolean; ancho?: '1/1' | '1/2' | '1/3';
}
export interface SeccionFormato { titulo: string; campos: CampoFormato[] }
export interface SchemaFormato { secciones?: SeccionFormato[] }
export const CATEGORIAS_FORMATO = ['msp', 'especialidad', 'odontologia', 'ocupacional', 'certificado', 'general'] as const;
export const esSchema = (c: unknown): c is SchemaFormato =>
  !!c && typeof c === 'object' && Array.isArray((c as SchemaFormato).secciones);
export interface Plantilla {
  id: string; especialidad_id: string | null; nombre: string;
  categoria?: string; codigo?: string | null; descripcion?: string | null;
  contenido: SoapContenido & SchemaFormato; es_activo: boolean; campos?: number;
}

export interface SignoVital {
  id?: string; presion_sistolica: number | null; presion_diastolica: number | null;
  frecuencia_cardiaca: number | null; frecuencia_resp: number | null; temperatura: number | null;
  spo2: number | null; peso: number | null; talla: number | null; glucosa: number | null;
  dolor: number | null; observaciones: string | null;
  fum?: string | null; perimetro_cefalico?: number | null;
}
export interface Diagnostico {
  id?: string; cie10_codigo: string | null; descripcion: string; tipo: string; es_principal: boolean;
}
export interface EvolucionItem {
  evolucion: {
    id: string; fecha: string; motivo: string | null; subjetivo: string | null; objetivo: string | null;
    analisis: string | null; plan: string | null; observaciones: string | null; firmada: boolean; firmada_at: string | null;
    plantilla_id?: string | null; datos?: Record<string, unknown> | null;
  };
  medico: string; especialidad: string | null; diagnosticos: Diagnostico[]; signos: SignoVital[];
}
export interface Problema {
  id: string; cie10_codigo: string | null; descripcion: string; estado: string; es_critico: boolean;
  fecha_inicio: string; fecha_resolucion: string | null; notas: string | null;
}

// Medicina ocupacional (Fase 2)
export interface Convenio {
  id: string; partner_id: string; tipo: string; comision_pct: number;
  contacto_nombre: string | null; contacto_telefono: string | null; contacto_email: string | null;
  observaciones: string | null; es_activo: boolean; razon_social: string; identificacion: string | null;
}
export const TIPOS_CONVENIO = [{ v: 'empresa', label: 'Empresa empleadora' }, { v: 'aseguradora', label: 'Aseguradora' }, { v: 'otro', label: 'Otro' }] as const;
export const TIPOS_EXAMEN_OCUP = [
  { v: 'preocupacional', label: 'Preocupacional' }, { v: 'periodico', label: 'Periódico' },
  { v: 'reintegro', label: 'Reintegro' }, { v: 'retiro', label: 'Retiro' }, { v: 'aptitud', label: 'Aptitud' },
] as const;
export const RESULTADOS_APTITUD = [
  { v: 'apto', label: 'Apto' }, { v: 'apto_restricciones', label: 'Apto con restricciones' },
  { v: 'no_apto', label: 'No apto' }, { v: 'aplazado', label: 'Aplazado' },
] as const;
export interface Aptitud {
  id: string; paciente_id: string; paciente: string; empleador_id: string | null; empleador: string | null;
  cargo: string | null; tipo_examen: string; resultado: string; restricciones: string | null;
  fecha: string; vigencia_hasta: string | null; medico_id: string | null; medico: string | null; vigente: boolean;
}
export interface DerivacionRow {
  evolucion_id: string; fecha: string; paciente: string; medico: string | null;
  derivador: string | null; convenio: string | null; convenio_tipo: string | null; comision_pct: number | null;
  base: number; monto_comision: number;
}
export interface Vacuna {
  id: string; vacuna: string; dosis: string | null; fecha: string; lote: string | null;
  via: string | null; sitio: string | null; responsable: string | null; proxima_fecha: string | null; observaciones: string | null;
}
export interface Historia {
  paciente: { id: string; fecha_nacimiento: string | null; genero: string | null; tipo_sangre: string | null; razon_social: string; identificacion: string | null } | null;
  alertas: { alergias: string | null; antecedentes: string | null; medicamentos: string | null; problemas_criticos: string[] };
  problemas: Problema[]; vacunas: Vacuna[]; evoluciones: EvolucionItem[];
}

// ---- Recetas ----
export interface RecetaItem {
  producto_id: string | null; medicamento: string; comercial: string | null; concentracion: string; forma: string;
  dosis: string; frecuencia: string; via: string; duracion_dias: number | null; cantidad: string; indicaciones: string;
}
export interface RecetaRow {
  id: string; codigo: string; fecha: string; tipo: string; estado: string; diagnostico: string | null;
  renovable: boolean; refills_total: number; refills_usados: number; valido_hasta: string | null;
  medico: string; items: number;
}
export interface RecetaDetalle extends CabeceraImpreso {
  receta: {
    id: string; codigo: string; codigo_verificacion: string; fecha: string; tipo: string; estado: string;
    diagnostico: string | null; indicaciones: string | null; valido_hasta: string | null; validez_dias: number | null;
    renovable: boolean; refills_total: number; refills_usados: number; firma_paciente_url: string | null;
    datos: {
      diagnosticos?: { cie10_codigo: string | null; descripcion: string; tipo?: string; es_principal?: boolean }[];
      proxima_cita?: string | null; recomendaciones?: string | null;
      alergias_farmacologicas?: string | null; signos_alarma?: string[] | null;
    } | null;
  };
  paciente: string; paciente_ident: string | null; paciente_edad: number | null; paciente_edad_meses?: number | null;
  paciente_mision: string | null; paciente_mision_codigo: string | null;
  medico: string; medico_licencia: string | null; medico_firma: string | null; medico_firma_modo?: string | null;
  especialidad: string | null;
  items: Array<RecetaItem & { id: string; orden: number }>;
}
export interface MedicamentoOpt { id: string; nombre: string; concentracion: string | null; presentacion: string | null }

export const itemRecetaVacio: RecetaItem = {
  producto_id: null, medicamento: '', comercial: null, concentracion: '', forma: '', dosis: '', frecuencia: '', via: 'oral', duracion_dias: null, cantidad: '', indicaciones: '',
};

// ---- Exámenes y Laboratorio ----
export interface ExamenParametro {
  id: string; codigo: string | null; nombre: string; unidad: string | null;
  valor_ref_min: number | null; valor_ref_max: number | null; valor_ref_texto: string | null; orden: number;
}
export interface Examen {
  id: string; codigo: string | null; nombre: string; categoria: string;
  subcategoria: string | null; especialidad_id: string | null; especialidad: string | null; tipo_muestra: string | null;
  modificadores: string | null; metodo: string | null; precio: number; notas: string | null;
  es_activo: boolean; sexo?: string | null; parametros: ExamenParametro[];
}

// Coherencia por sexo (691): un ítem con sexo 'F'/'M' solo aplica a pacientes
// de ese género; NULL = ambos. Género 'O' (otro) o sin dato → se muestra todo.
export const compatibleConSexo = (sexo: string | null | undefined, genero: string | null | undefined) =>
  !sexo || !genero || (genero !== 'F' && genero !== 'M') || sexo === genero;
export interface SolicitudRow {
  id: string; codigo: string; fecha: string; estado: string; prioridad: string; laboratorio: string | null;
  fecha_resultado: string | null; paciente: string; medico: string; items: number;
}
export interface ResultadoValor {
  id?: string; parametro_id: string | null; parametro_nombre: string; valor: number | null; valor_texto: string | null;
  unidad: string | null; valor_ref_min: number | null; valor_ref_max: number | null; valor_ref_texto: string | null; flag: string;
}
export interface SolicitudItem {
  item: { id: string; examen_id: string; examen_nombre: string; estado: string; resultado_url: string | null; observaciones: string | null };
  parametros: ExamenParametro[]; resultados: ResultadoValor[];
}
export interface SolicitudDetalle extends CabeceraImpreso {
  solicitud: { id: string; codigo: string; fecha: string; estado: string; prioridad: string; diagnostico: string | null; laboratorio: string | null; notas: string | null };
  paciente: string; paciente_ident: string | null;
  paciente_edad?: number | null; paciente_edad_meses?: number | null; paciente_sexo?: string | null;
  medico: string; medico_licencia?: string | null; especialidad?: string | null;
  // `nombre`, `codigo` y `categoria` los añadió el script 851 para poder
  // imprimir la orden: sin la categoría no se sabe cómo titularla.
  items: Array<SolicitudItem & {
    nombre?: string | null; codigo?: string | null; categoria?: string | null;
    tipo_muestra?: string | null; preparacion?: string | null;
  }>;
}
export interface SerieRow { fecha: string; valor: number; unidad: string | null; valor_ref_min: number | null; valor_ref_max: number | null; flag: string }

export const CATEGORIA_EXAMEN = ['laboratorio', 'imagenologia', 'patologia', 'microbiologia', 'cardiologia', 'otro'];
export const ESTADO_SOLICITUD: Record<string, { label: string; cls: string }> = {
  solicitada: { label: 'Solicitada', cls: 'bg-secondary text-secondary-foreground' },
  en_proceso: { label: 'En proceso', cls: 'bg-pendiente-soft text-pendiente' },
  lista: { label: 'Lista', cls: 'bg-estable-soft text-estable' },
  entregada: { label: 'Entregada', cls: 'bg-completado-soft text-completado' },
  anulada: { label: 'Anulada', cls: 'bg-urgente-soft text-urgente' },
};
export const FLAG_CLS: Record<string, string> = {
  normal: 'text-foreground', alto: 'text-urgente font-semibold', bajo: 'text-primary font-semibold', critico: 'text-urgente font-bold',
};

// ---- Telemedicina ----
export interface TeleWorklistRow {
  cita_id: string; teleconsulta_id: string | null; fecha: string; hora_inicio: string;
  estado: string; consentimiento_aceptado: boolean | null; checkin_at: string | null; admitido_at: string | null;
  resultado: string | null; tarifa: number | string | null; pago_estado: string | null;
  paciente: string; medico: string; medico_id: string; motivo: string | null;
}
export interface TeleDetalle {
  teleconsulta: {
    id: string; estado: string; meet_url: string | null; codigo_acceso: string; acceso_expira: string | null;
    consentimiento_aceptado: boolean; grabacion_consentida: boolean; grabacion_url: string | null;
    duracion_seg: number | null; calidad: string | null; iniciada_at: string | null; finalizada_at: string | null;
    checkin_at: string | null; admitido_at: string | null; resultado: string | null;
  };
  paciente: string; paciente_ident: string | null; medico: string; medico_id: string; paciente_id: string;
  cita_fecha: string; cita_hora: string;
}
export interface TeleMensaje { id: string; emisor: string; texto: string | null; archivo_url: string | null; archivo_nombre: string | null; created_at: string }
export interface TelePacienteRow {
  teleconsulta_id: string | null; fecha: string; hora_inicio: string; cita_id: string; estado: string;
  consentimiento_aceptado: boolean | null; checkin_at: string | null; admitido_at: string | null;
  tarifa: number | string | null; pago_estado: string | null;
  medico: string; especialidad: string | null; turno_antes: number;
}
export const ESTADO_TELE: Record<string, { label: string; cls: string }> = {
  programada: { label: 'Programada', cls: 'bg-secondary text-secondary-foreground' },
  en_sala: { label: 'En sala', cls: 'bg-pendiente-soft text-pendiente' },
  en_curso: { label: 'En curso', cls: 'bg-primary/15 text-primary' },
  finalizada: { label: 'Finalizada', cls: 'bg-completado-soft text-completado' },
  cancelada: { label: 'Cancelada', cls: 'bg-urgente-soft text-urgente' },
};
export const CONSENT_ITEMS = [
  'Acepto recibir atención médica por videollamada (telemedicina).',
  'Entiendo las limitaciones de la consulta virtual frente a la presencial.',
  'Autorizo el tratamiento de mis datos de salud para esta atención.',
];

// ---- Calendarios corporativos ----
export interface Recurso {
  id: string; codigo: string | null; nombre: string; tipo: string; ubicacion: string | null;
  capacidad: number | null; color: string | null; es_activo: boolean; notas: string | null;
}
export interface CalItem {
  clase: 'evento' | 'reserva'; id: string; titulo: string; tipo: string;
  fecha_inicio: string; fecha_fin: string; todo_el_dia: boolean;
  recurso_id: string | null; recurso: string | null; color: string | null; estado: string | null;
}
export interface Ocupacion {
  id: string; nombre: string; tipo: string; ubicacion: string | null; color: string | null;
  ocupado: boolean; ocupado_por: string | null; hasta: string | null;
}
export const TIPO_RECURSO = ['consultorio', 'quirofano', 'laboratorio', 'sala', 'equipo', 'ambulancia', 'otro'];
export const TIPO_EVENTO_CORP = ['feriado', 'capacitacion', 'reunion', 'mantenimiento', 'bloqueo', 'otro'];
export const COLOR_EVENTO: Record<string, string> = {
  feriado: 'bg-urgente-soft text-urgente', capacitacion: 'bg-primary/15 text-primary', reunion: 'bg-accent/15 text-accent',
  mantenimiento: 'bg-pendiente-soft text-pendiente', bloqueo: 'bg-muted text-muted-foreground', otro: 'bg-secondary text-secondary-foreground',
  reserva: 'bg-estable-soft text-estable',
};

export const TIPO_DIAGNOSTICO = ['presuntivo', 'definitivo'];
export const ESTADO_PROBLEMA = ['activo', 'cronico', 'resuelto'];

export const calcEdad = (fecha: string | null): string => {
  if (!fecha) return '—';
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return '—';
  const hoy = new Date();
  let edad = hoy.getFullYear() - d.getFullYear();
  const m = hoy.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < d.getDate())) edad--;
  return `${edad} años`;
};

// ---- Fase 3: recetas inteligentes, certificados, firmas ----

// Medicamento favorito del médico (plantilla rápida de prescripción).
export interface Favorito extends RecetaItem { id: string; veces_usado: number }

export interface CertificadoRow {
  id: string; codigo: string; fecha: string; tipo: string; estado: string;
  diagnostico: string | null; dias_reposo: number | null; reposo_desde: string | null; reposo_hasta: string | null; medico: string;
}
export interface Maternidad {
  fecha_ingreso?: string | null; fecha_nacimiento?: string | null; fecha_alta?: string | null;
  rn_nombre?: string | null; rn_sexo?: string | null; rn_peso?: string | null; rn_talla?: string | null;
  adjunto_url?: string | null;
}
export interface CertificadoDetalle {
  certificado: {
    id: string; codigo: string; codigo_verificacion: string; fecha: string; tipo: string; estado: string;
    diagnostico: string | null; cie10_codigo: string | null; cuerpo: string | null;
    dias_reposo: number | null; reposo_desde: string | null; reposo_hasta: string | null; contingencia: string | null;
    maternidad: Maternidad | null; firma_paciente_url: string | null;
  };
  paciente: string; paciente_ident: string | null; paciente_edad: number | null;
  paciente_mision: string | null; paciente_mision_codigo: string | null;
  paciente_direccion: string | null; paciente_telefono: string | null; paciente_institucion: string | null;
  paciente_puesto: string | null; paciente_hc: string | null;
  medico: string; medico_cedula: string | null; medico_licencia: string | null;
  medico_firma: string | null; medico_firma_modo?: string | null;
  especialidad: string | null;
  empresa: string; empresa_ruc: string | null; empresa_direccion: string | null;
  empresa_telefono: string | null; empresa_email: string | null; empresa_logo: string | null;
}
export const TIPOS_CERTIFICADO: { v: string; label: string }[] = [
  { v: 'reposo', label: 'Reposo médico' },
  { v: 'aptitud', label: 'Aptitud / salud' },
  { v: 'asistencia', label: 'Asistencia a consulta' },
  { v: 'general', label: 'General' },
];
// Tipos de contingencia oficiales (IESS/MSP Ecuador) para el certificado de reposo.
export const CONTINGENCIAS_REPOSO: { v: string; label: string }[] = [
  { v: 'enfermedad_general', label: 'Enfermedad general' },
  { v: 'enfermedad_catastrofica', label: 'Enfermedad catastrófica' },
  { v: 'maternidad', label: 'Maternidad' },
  { v: 'accidente_grave', label: 'Accidente grave (debidamente certificado)' },
  { v: 'presuncion_accidente_trabajo', label: 'Presunción de accidente de trabajo' },
  { v: 'presuncion_enfermedad', label: 'Presunción de enfermedad' },
];

// Número entero (0–999999) a palabras en español. Para "cantidad en número y letras".
const UNIDADES = ['cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve', 'diez',
  'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve', 'veinte',
  'veintiuno', 'veintidós', 'veintitrés', 'veinticuatro', 'veinticinco', 'veintiséis', 'veintisiete', 'veintiocho', 'veintinueve'];
const DECENAS = ['', '', '', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

function centenasALetras(n: number): string {
  if (n === 0) return '';
  if (n === 100) return 'cien';
  let out = '';
  const c = Math.floor(n / 100), resto = n % 100;
  if (c > 0) out += CENTENAS[c] + ' ';
  if (resto > 0) {
    if (resto < 30) out += UNIDADES[resto];
    else { const d = Math.floor(resto / 10), u = resto % 10; out += DECENAS[d] + (u > 0 ? ' y ' + UNIDADES[u] : ''); }
  }
  return out.trim();
}

export function numeroALetras(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '';
  n = Math.floor(n);
  if (n === 0) return 'cero';
  if (n < 1000) return centenasALetras(n);
  const miles = Math.floor(n / 1000), resto = n % 1000;
  const pref = miles === 1 ? 'mil' : `${centenasALetras(miles)} mil`;
  return (resto > 0 ? `${pref} ${centenasALetras(resto)}` : pref).trim();
}

// Antepone el número en letras a una cantidad de texto libre: "15 tabletas" → "15 (quince) tabletas".
export function cantidadEnLetras(texto: string | null | undefined): string {
  const t = (texto ?? '').trim();
  if (!t) return '';
  const m = t.match(/^(\d+)(.*)$/);
  if (!m) return t;
  const n = Number(m[1]);
  return `${m[1]} (${numeroALetras(n)})${m[2]}`;
}

const MESES_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
// Fecha ISO (YYYY-MM-DD) → "18/08/2026 (dieciocho de agosto de dos mil veintiséis)".
// Para el certificado de reposo oficial (fechas en números Y letras).
export function fechaEnLetras(iso: string | null | undefined): string {
  const s = (iso ?? '').slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const [, y, mm, dd] = m;
  const dia = Number(dd), mesIdx = Number(mm) - 1, anio = Number(y);
  const num = `${dd}/${mm}/${y}`;
  const letras = `${numeroALetras(dia)} de ${MESES_ES[mesIdx] ?? mm} de ${numeroALetras(anio)}`;
  return `${num} (${letras})`;
}

// Misión del paciente (= su Centro de Costo) resaltada para los impresos clínicos.
// Se muestra en la misma línea del nombre, en tamaño distintivo y recuadrada.
export function MisionBadge({ codigo, nombre, className }: { codigo?: string | null; nombre?: string | null; className?: string }) {
  const n = (nombre ?? '').trim();
  if (!n) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded border-2 border-black px-2 py-0.5 text-sm font-extrabold uppercase tracking-wide leading-none align-middle ${className ?? ''}`}>
      <span className="text-[10px] font-semibold opacity-70">Misión</span>
      {codigo ? `${codigo} · ` : ''}{n}
    </span>
  );
}

// ---- Fase 4: IMC, historial de exámenes ----

// Clasificación del IMC (OMS) con clase de color para el chip.
export function clasificacionIMC(imc: number | null): { label: string; cls: string } | null {
  if (imc == null) return null;
  if (imc < 18.5) return { label: 'Bajo peso', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
  if (imc < 25) return { label: 'Normal', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' };
  if (imc < 30) return { label: 'Sobrepeso', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' };
  if (imc < 35) return { label: 'Obesidad I', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' };
  if (imc < 40) return { label: 'Obesidad II', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };
  return { label: 'Obesidad III', cls: 'bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-200' };
}

// ---- IMC pediátrico por percentiles OMS/CDC (script 388) ----
export interface RefIMCRow { fuente: string; edad_meses: number; p3: number | null; p85: number | null; p97: number | null }

export const mesesEntre = (fnac?: string | null, fecha?: string | null): number | null => {
  if (!fnac || !fecha) return null;
  const n = new Date(`${fnac.slice(0, 10)}T00:00:00`);
  const f = new Date(`${fecha.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(n.getTime()) || Number.isNaN(f.getTime()) || f < n) return null;
  return (f.getTime() - n.getTime()) / (30.4375 * 24 * 3600e3);
};

// Percentiles de IMC-para-la-edad del sexo dado (OMS 0-60m + CDC 24-240m); cacheado.
export function useRefIMC(sexo?: string | null) {
  const valido = sexo === 'M' || sexo === 'F';
  return useQuery<RefIMCRow[]>({
    queryKey: ['med-ref-imc', sexo],
    queryFn: () => med.list<RefIMCRow>('crecimiento_referencia', `?indicador=eq.imc_edad&sexo=eq.${sexo}&select=fuente,edad_meses,p3,p85,p97&order=edad_meses`),
    staleTime: Infinity,
    enabled: valido,
  });
}

// <18 años: clasifica por percentiles (<P3 bajo peso · P3-P85 normal · ≥P85
// sobrepeso · ≥P97 obesidad); sin edad/referencia o ≥18 → cortes de adulto.
export function clasificacionIMCEdad(imc: number | null, edadMeses: number | null | undefined, ref: RefIMCRow[] | undefined): { label: string; cls: string } | null {
  if (imc == null) return null;
  if (edadMeses == null || edadMeses >= 216 || !ref?.length) return clasificacionIMC(imc);
  const pool = ref.filter((r) => (edadMeses <= 60 ? r.fuente === 'OMS' : r.fuente === 'CDC'));
  const filas = pool.length ? pool : ref;
  const fila = filas.reduce((a, b) => (Math.abs(b.edad_meses - edadMeses) < Math.abs(a.edad_meses - edadMeses) ? b : a));
  if (Math.abs(fila.edad_meses - edadMeses) > 3 || fila.p3 == null || fila.p85 == null || fila.p97 == null) return clasificacionIMC(imc);
  if (imc < fila.p3) return { label: `Bajo peso (<P3 ${fila.fuente})`, cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
  if (imc < fila.p85) return { label: `Normal (P3\u2013P85 ${fila.fuente})`, cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' };
  if (imc < fila.p97) return { label: `Sobrepeso (\u2265P85 ${fila.fuente})`, cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' };
  return { label: `Obesidad (\u2265P97 ${fila.fuente})`, cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };
}

// Una fila del historial de resultados (med_historial_resultados).
export interface HistorialResultado {
  solicitud_id: string; codigo: string; fecha: string; examen: string; parametro: string;
  valor: number | null; valor_texto: string | null; unidad: string | null;
  valor_ref_min: number | null; valor_ref_max: number | null; valor_ref_texto: string | null; flag: string;
}
